import { CheckOutlined, LoadingOutlined } from "@ant-design/icons"
import { Dropdown, Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../../../../Icon"
import { GROUP_META, MATCH_TYPE_LABEL, STATUS_META } from "../../constants/constants"
import { selectMatchTypeSaving, selectStatusSaving } from "../../redux/business.slice"

import styles from "./descriptor-header-addon.module.less"

const { Text } = Typography

// Support's two editable controls on a descriptor row (CHA1-1975 BLOCK 3.4/3.5).
// Visually this matches the older admin Enrollment addon, but it is deliberately
// its own component: that one hardcodes Enrollment's status list (which has
// `invalid_arns` where Receipt has `invalid_codes`) and keys match type by label
// rather than by the `exact` / `starts_with` the Receipt API takes.
//
// Both dropdowns stay enabled while a change is in flight — support may change a
// value again before the previous one is confirmed, and the last write wins. The
// trigger swaps its chevron for a spinner meanwhile, so a write that is only slow
// is not read as one that did nothing (CHA1-2614).
//
// The two controls differ on re-selecting the current value: match type treats it
// as a no-op, status still sends (see each menu's onClick).

// All 13, in STATUS_META order — support may set any status from any other, there
// is no transition graph to filter by (CHA1-1374 BLOCK 1.1). Mirrors the server's
// ReceiptDescriptorStatus.
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([key, meta]) => ({
  key,
  label: meta.label,
  groupClass: GROUP_META[meta.group]?.class
}))

const MATCH_TYPE_OPTIONS = Object.entries(MATCH_TYPE_LABEL).map(([key, label]) => ({ key, label }))

// One row of a dropdown: the option, plus a tick when it is the current value.
const optionLabel = (label, selected, t, dot) => (
  <Flex align="center" justify="space-between" gap={24}>
    <Flex align="center" gap={14}>
      {dot ? <span className={cls(styles.dot, styles[dot])} /> : null}
      <span>{t(label)}</span>
    </Flex>
    {selected ? <CheckOutlined className={styles.check} /> : null}
  </Flex>
)

// The chevron's slot, so the trigger keeps its width while a write is in flight.
const triggerAffordance = (saving) =>
  saving ? (
    <LoadingOutlined className={styles.arrow} />
  ) : (
    <Icon type="chevron_down_small" className={styles.arrow} />
  )

const DescriptorHeaderAddon = ({ row, onStatusChange, onMatchTypeChange }) => {
  const { t } = useTranslation()
  const statusSaving = useSelector(selectStatusSaving(row?.record_id))
  const matchTypeSaving = useSelector(selectMatchTypeSaving(row?.record_id))

  const status = row?.current_status
  const matchType = row?.match_type
  const statusGroupClass = GROUP_META[STATUS_META[status]?.group]?.class

  const statusMenu = {
    items: STATUS_OPTIONS.map(({ key, label, groupClass }) => ({
      key,
      label: optionLabel(label, key === status, t, groupClass)
    })),
    // Re-picking the current status is deliberately NOT short-circuited: the
    // server's empty-transition guard suppresses the email but still recomputes
    // the program/BA rollups (CHA1-1374 BLOCK 1.5), so swallowing the click here
    // would skip a recalculation support asked for.
    onClick: ({ key }) => onStatusChange?.(key)
  }

  const matchTypeMenu = {
    items: MATCH_TYPE_OPTIONS.map(({ key, label }) => ({
      key,
      label: optionLabel(label, key === matchType, t)
    })),
    // "Selecting the value that is already set is a no-op — nothing is sent and
    // nothing is logged" (BLOCK 3.5).
    onClick: ({ key }) => key !== matchType && onMatchTypeChange?.(key)
  }

  return (
    <Flex align="center" gap={8} className={styles.addon}>
      {/* Hidden entirely when no record of this descriptor is Active — the API
          answers `match_type: null` in exactly that case (BLOCK 3.5). */}
      {matchType ? (
        <>
          <Dropdown trigger={["click"]} menu={matchTypeMenu} overlayClassName={styles.menu}>
            <Flex
              align="center"
              gap={6}
              // Dimmed while the status write runs too: the control itself is
              // about to disappear or stay depending on how that resolves.
              className={cls(styles.trigger, (matchTypeSaving || statusSaving) && styles.saving)}
              aria-busy={matchTypeSaving || undefined}
              data-testid={`deflect-business-match-type-${row.record_id}`}
            >
              <Text type="secondary" strong>
                {t(MATCH_TYPE_LABEL[matchType] || matchType)}
              </Text>
              {triggerAffordance(matchTypeSaving)}
            </Flex>
          </Dropdown>
          <span className={styles.divider} />
        </>
      ) : null}

      <Dropdown trigger={["click"]} menu={statusMenu} overlayClassName={styles.menu}>
        <Flex
          align="center"
          gap={6}
          className={cls(
            styles.trigger,
            styles.statusTrigger,
            styles[statusGroupClass],
            statusSaving && styles.saving
          )}
          aria-busy={statusSaving || undefined}
          data-testid={`deflect-business-status-${row.record_id}`}
        >
          <Text strong>{t(STATUS_META[status]?.label || status)}</Text>
          {triggerAffordance(statusSaving)}
        </Flex>
      </Dropdown>
    </Flex>
  )
}

export default DescriptorHeaderAddon
